<template>
    <div class="sensor-history">
        <div class="sensor-history-toggle d-flex align-items-center" v-on:click="toggle">
            <i class="mdi mdi-chart-line mr-2"></i>
            <span>{{ $t('HISTORY_TOGGLE') }}</span>
            <span class="ml-auto small text-muted mr-2">{{ $t('HISTORY_WINDOW') }}</span>
            <i class="mdi" v-bind:class="open ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
        </div>

        <div v-if="open" class="px-3 pb-3">
            <div v-if="loading" class="text-muted small">{{ $t('HISTORY_LOADING') }}</div>
            <div v-else-if="failed" class="text-danger small">{{ $t('HISTORY_FAILED') }}</div>
            <div v-else-if="series.length === 0" class="text-muted small">{{ $t('HISTORY_EMPTY') }}</div>
            <series-chart v-else :series="series" :height="340"/>
        </div>
    </div>
</template>

<script>
import Api from '@/api/Modules';
import SeriesChart from './SeriesChart.vue';

// module_values stores every measurement under its own name, and the two sensors
// spell theirs differently - the station in snake case, the air sensor in camel.
// Anything not named here falls back to its raw key rather than being hidden.
// Everything shares one y axis, so a measurement that lives on a different order
// of magnitude would flatten the rest into a line along the bottom. Those start
// hidden and are one click away in the legend.
const OFF_SCALE = [
    'illuminance', 'pressure', 'precipitation', 'wind_direction',
    'CarbonDioxide', 'battery',
];

const LABELS = {
    temperature: 'Temperature',
    analogValue: 'Analog',
    dew_point: 'Dew point',
    wind_speed: 'Wind speed',
    gust_speed: 'Gust speed',
    wind_direction: 'Wind direction',
    rain_status: 'Rain',
    uv: 'UV index',
    illuminance: 'Illuminance',
    pressure: 'Pressure',
    humidity: 'Humidity',
    battery: 'Battery',
    precipitation: 'Precipitation',
    CarbonDioxide: 'CO2',
    MHZTemperature: 'CO2 sensor temperature',
    Temperature: 'Temperature',
    Humidity: 'Humidity',
    DewPoint: 'Dew point',
};

export default
{
    name: 'SensorHistory',
    components:
    {
        'series-chart': SeriesChart,
    },
    props:
    {
        moduleId:
        {
            type: [Number, String],
            required: true,
        },
    },
    data()
    {
        return {
            open: false,
            loaded: false,
            loading: false,
            failed: false,
            series: [],
        };
    },
    watch:
    {
        moduleId()
        {
            const me = this;

            me.loaded = false;
            me.series = [];

            if (me.open)
            {
                me.load();
            }
        },
    },
    methods:
    {
        // nothing is fetched until someone opens the panel, and then only once
        toggle()
        {
            const me = this;

            me.open = !me.open;

            if (me.open && !me.loaded)
            {
                me.load();
            }
        },
        label(key)
        {
            return LABELS[key] || key.replace(/_/g, ' ');
        },
        load()
        {
            const me = this;

            me.loading = true;
            me.failed = false;

            Api.getModuleHistory({ moduleId: me.moduleId }, (data) =>
            {
                me.loading = false;

                if (!data || data.error)
                {
                    // say so rather than sit on an empty white panel
                    me.failed = true;
                    return;
                }

                const series = [];
                const named = Object.keys(data.values || {});

                // module_log carries temperature and analogValue as columns. Only
                // show a column that actually holds something - most modules have
                // no analog input at all and an empty chart says nothing.
                for (const field of ['temperature', 'analogValue'])
                {
                    // A sensor that logs its temperature by name as well would
                    // otherwise appear twice, once from each table, under the same
                    // label. The named row is the more precise of the two.
                    if (named.some((key) => key.toLowerCase() === field.toLowerCase()))
                    {
                        continue;
                    }

                    const points = (data.log || [])
                        .filter((row) => row[field] != null)
                        .map((row) => ({ dateTime: row.dateTime, value: row[field] }));

                    if (points.length > 0)
                    {
                        series.push({
                            key: field,
                            label: me.label(field),
                            points,
                            hidden: (OFF_SCALE.indexOf(field) !== -1),
                        });
                    }
                }

                for (const key of named.slice().sort())
                {
                    series.push({
                        key,
                        label: me.label(key),
                        points: data.values[key],
                        hidden: (OFF_SCALE.indexOf(key) !== -1),
                    });
                }

                me.series = series;
                me.loaded = true;
            });
        },
    },
};
</script>
